import { useState, useCallback, useEffect } from 'react';
import { Upload, FileSpreadsheet, Edit3, Trash2, Loader2, Search, Filter, X, Check, DollarSign, Building2, Calendar, FileText, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const Cheltuieli = () => {
    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [importing, setImporting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [categories, setCategories] = useState([]);
    
    // Edit dialog
    const [showEditDialog, setShowEditDialog] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const [editDescription, setEditDescription] = useState('');
    const [editCategory, setEditCategory] = useState('');
    const [editBaza, setEditBaza] = useState('');
    const [editTva, setEditTva] = useState('');
    const [editTotal, setEditTotal] = useState('');
    const [autoCalculateTotal, setAutoCalculateTotal] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Import results
    const [importResults, setImportResults] = useState(null);
    
    // Delete all dialog
    const [showDeleteAllDialog, setShowDeleteAllDialog] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deletingAll, setDeletingAll] = useState(false);

    // Load expenses
    const loadExpenses = useCallback(async () => {
        setLoading(true);
        try {
            const response = await api.get('/expenses/v2');
            setExpenses(response.data || []);
        } catch (err) {
            console.error('Error loading expenses:', err);
            toast.error('Eroare la încărcarea cheltuielilor');
        } finally {
            setLoading(false);
        }
    }, []);

    // Load categories
    const loadCategories = useCallback(async () => {
        try {
            const response = await api.get('/expenses/v2/categories');
            setCategories(response.data || []);
        } catch (err) {
            console.error('Error loading categories:', err);
        }
    }, []);

    useEffect(() => {
        loadExpenses();
        loadCategories();
    }, [loadExpenses, loadCategories]);

    // Handle Excel import
    const handleImport = useCallback(async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setImporting(true);
        setImportResults(null);
        
        try {
            const formData = new FormData();
            formData.append('file', file);
            
            // clear_existing=false: nu șterge datele existente, verifică duplicatele prin nr_nir
            const response = await api.post('/expenses/v2/import?clear_existing=false', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            
            setImportResults(response.data);
            toast.success(response.data.message);
            
            // Reload expenses
            await loadExpenses();
            await loadCategories();
        } catch (err) {
            console.error('Import error:', err);
            toast.error('Eroare la import: ' + (err.response?.data?.detail || err.message));
        } finally {
            setImporting(false);
            e.target.value = ''; // Reset file input
        }
    }, [loadExpenses, loadCategories]);

    // Open edit dialog
    const openEditDialog = (item) => {
        setEditItem(item);
        setEditDescription(item.description || '');
        setEditCategory(item.category || '');
        setEditBaza(item.baza?.toString() || '0');
        setEditTva(item.tva?.toString() || '0');
        setEditTotal(item.total?.toString() || item.suma?.toString() || '0');
        setAutoCalculateTotal(true);
        setShowEditDialog(true);
    };

    // Auto-calculate total when baza or tva changes
    const handleBazaChange = (value) => {
        setEditBaza(value);
        if (autoCalculateTotal) {
            const baza = parseFloat(value) || 0;
            const tva = parseFloat(editTva) || 0;
            setEditTotal((baza + tva).toFixed(2));
        }
    };

    const handleTvaChange = (value) => {
        setEditTva(value);
        if (autoCalculateTotal) {
            const baza = parseFloat(editBaza) || 0;
            const tva = parseFloat(value) || 0;
            setEditTotal((baza + tva).toFixed(2));
        }
    };

    const handleTotalChange = (value) => {
        setEditTotal(value);
        setAutoCalculateTotal(false); // Disable auto-calc when user manually edits total
    };

    // Save edit
    const handleSaveEdit = useCallback(async () => {
        if (!editItem) return;
        
        setSaving(true);
        try {
            await api.put(`/expenses/v2/${editItem.id}`, {
                description: editDescription,
                category: editCategory,
                baza: parseFloat(editBaza) || 0,
                tva: parseFloat(editTva) || 0,
                total: parseFloat(editTotal) || 0
            });
            
            toast.success('Cheltuială actualizată');
            setShowEditDialog(false);
            setEditItem(null);
            
            // Reload
            await loadExpenses();
            await loadCategories();
        } catch (err) {
            toast.error('Eroare: ' + (err.response?.data?.detail || err.message));
        } finally {
            setSaving(false);
        }
    }, [editItem, editDescription, editCategory, editBaza, editTva, editTotal, loadExpenses, loadCategories]);

    // Delete expense
    const handleDelete = useCallback(async (id) => {
        if (!confirm('Sigur vrei să ștergi această cheltuială?')) return;
        
        try {
            await api.delete(`/expenses/v2/${id}`);
            toast.success('Cheltuială ștearsă');
            await loadExpenses();
        } catch (err) {
            toast.error('Eroare: ' + (err.response?.data?.detail || err.message));
        }
    }, [loadExpenses]);

    // Delete ALL expenses
    const handleDeleteAll = useCallback(async () => {
        if (deleteConfirmText !== 'STERGE') {
            toast.error('Trebuie să scrii "STERGE" pentru a confirma');
            return;
        }
        
        setDeletingAll(true);
        try {
            const response = await api.delete('/expenses/v2/all/clear?confirmation=STERGE');
            toast.success(`${response.data.deleted_count} cheltuieli șterse`);
            setShowDeleteAllDialog(false);
            setDeleteConfirmText('');
            await loadExpenses();
            await loadCategories();
        } catch (err) {
            toast.error('Eroare: ' + (err.response?.data?.detail || err.message));
        } finally {
            setDeletingAll(false);
        }
    }, [deleteConfirmText, loadExpenses, loadCategories]);

    // Filter expenses
    const filteredExpenses = expenses.filter(exp => {
        const matchesSearch = !searchTerm || 
            exp.nr_factura?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exp.furnizor?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            exp.description?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCategory = !categoryFilter || exp.category === categoryFilter;
        
        return matchesSearch && matchesCategory;
    });

    const [expandedMonths, setExpandedMonths] = useState({});
    const toggleExpenseMonth = (key) => setExpandedMonths(prev => ({ ...prev, [key]: !prev[key] }));
    const MONTHS_RO = ['Ianuarie', 'Februarie', 'Martie', 'Aprilie', 'Mai', 'Iunie', 'Iulie', 'August', 'Septembrie', 'Octombrie', 'Noiembrie', 'Decembrie'];
    const fmtRon = (v) => `${(v || 0).toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} RON`;
    const expenseMonths = Object.entries(filteredExpenses.reduce((acc, exp) => {
        const key = exp.date && /^\d{4}-\d{2}/.test(exp.date) ? exp.date.slice(0, 7) : 'zzzz';
        (acc[key] = acc[key] || []).push(exp);
        return acc;
    }, {}))
        .sort((a, b) => (a[0] === 'zzzz' ? 1 : b[0] === 'zzzz' ? -1 : b[0].localeCompare(a[0])))
        .map(([key, list]) => {
            const label = key === 'zzzz' ? 'Fără dată' : `${MONTHS_RO[parseInt(key.slice(5, 7), 10) - 1]} ${key.slice(0, 4)}`;
            const totals = list.reduce((t, e) => {
                const total = e.total ?? e.suma ?? 0;
                const tva = e.tva ?? 0;
                t.baza += e.baza ?? (total - tva);
                t.tva += tva;
                t.total += total;
                return t;
            }, { baza: 0, tva: 0, total: 0 });
            return { key, label, list, totals };
        });

    // Calculate totals
    const totalSum = filteredExpenses.reduce((sum, exp) => sum + (exp.suma || 0), 0);
    const uncategorizedCount = expenses.filter(e => !e.category).length;

    return (
        <div className="min-h-screen bg-[#F8F9FA]">
            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="mb-6 flex justify-between items-start">
                    <div>
                        <h1 className="text-2xl font-bold text-zinc-900">Cheltuieli</h1>
                        <p className="text-zinc-600 text-sm">Import și gestionare cheltuieli din Excel</p>
                    </div>
                    <div className="flex gap-2">
                        {/* Delete All Button */}
                        {expenses.length > 0 && (
                            <Button
                                variant="outline"
                                onClick={() => setShowDeleteAllDialog(true)}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid="delete-all-expenses-btn"
                            >
                                <Trash2 size={18} className="mr-2" />
                                Șterge Toate ({expenses.length})
                            </Button>
                        )}
                        
                        {/* Import Button */}
                        <label className="cursor-pointer">
                            <input 
                                type="file" 
                                accept=".xlsx,.xls" 
                                onChange={handleImport} 
                                className="hidden" 
                                disabled={importing}
                            />
                            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-white ${
                                importing ? 'bg-zinc-400' : 'bg-violet-600 hover:bg-violet-700'
                            }`}>
                                {importing ? <Loader2 className="animate-spin" size={18} /> : <Upload size={18} />}
                                {importing ? 'Se importă...' : 'Import Excel'}
                            </div>
                        </label>
                    </div>
                </div>

                {/* Import Results */}
                {importResults && (
                    <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                        <div className="flex justify-between items-start">
                            <div>
                                <h3 className="font-semibold text-emerald-800">Import finalizat</h3>
                                <p className="text-sm text-emerald-700">
                                    {importResults.imported} înregistrări noi, {importResults.skipped} ignorate
                                </p>
                            </div>
                            <button onClick={() => setImportResults(null)} className="text-emerald-600 hover:text-emerald-800">
                                <X size={18} />
                            </button>
                        </div>
                    </div>
                )}

                {/* Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                            <FileText size={16} />
                            Total Cheltuieli
                        </div>
                        <p className="text-2xl font-bold text-zinc-900">{expenses.length}</p>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                            <DollarSign size={16} />
                            Sumă Totală
                        </div>
                        <p className="text-2xl font-bold text-zinc-900">{totalSum.toLocaleString('ro-RO')} RON</p>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 text-zinc-500 text-sm mb-1">
                            <Building2 size={16} />
                            Furnizori Unici
                        </div>
                        <p className="text-2xl font-bold text-zinc-900">
                            {new Set(expenses.map(e => e.furnizor)).size}
                        </p>
                    </div>
                    <div className="bg-white border rounded-xl p-4">
                        <div className="flex items-center gap-2 text-amber-500 text-sm mb-1">
                            <Filter size={16} />
                            Necategorizate
                        </div>
                        <p className="text-2xl font-bold text-amber-600">{uncategorizedCount}</p>
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white border rounded-xl p-4 mb-4 flex gap-4 items-center flex-wrap">
                    <div className="flex-1 min-w-[200px]">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" size={18} />
                            <Input
                                placeholder="Caută după nr factură, furnizor, descriere..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={(e) => setCategoryFilter(e.target.value)}
                        className="border rounded-lg px-3 py-2 text-sm min-w-[150px]"
                    >
                        <option value="">Toate categoriile</option>
                        <option value="">-- Necategorizate --</option>
                        {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                        ))}
                    </select>
                    {(searchTerm || categoryFilter) && (
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => { setSearchTerm(''); setCategoryFilter(''); }}
                        >
                            <X size={14} className="mr-1" />
                            Resetează
                        </Button>
                    )}
                    <span className="text-sm text-zinc-500">
                        {filteredExpenses.length} din {expenses.length}
                    </span>
                </div>

                {/* Table */}
                <div className="bg-white border rounded-xl overflow-hidden">
                    {loading ? (
                        <div className="p-8 text-center">
                            <Loader2 className="animate-spin mx-auto text-zinc-400" size={32} />
                            <p className="text-zinc-500 mt-2">Se încarcă...</p>
                        </div>
                    ) : filteredExpenses.length === 0 ? (
                        <div className="p-8 text-center">
                            <FileSpreadsheet className="mx-auto text-zinc-300" size={48} />
                            <p className="text-zinc-500 mt-2">
                                {expenses.length === 0 
                                    ? 'Nu există cheltuieli. Importă un fișier Excel.' 
                                    : 'Nu s-au găsit cheltuieli cu filtrele selectate.'}
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-50 border-b">
                                    <tr>
                                        <th className="text-left px-4 py-3 font-medium">Nr Factură</th>
                                        <th className="text-left px-4 py-3 font-medium">Furnizor</th>
                                        <th className="text-left px-4 py-3 font-medium">Data</th>
                                        <th className="text-right px-4 py-3 font-medium">Sumă</th>
                                        <th className="text-left px-4 py-3 font-medium">Descriere</th>
                                        <th className="text-left px-4 py-3 font-medium">Categorie</th>
                                        <th className="text-center px-4 py-3 font-medium w-20">Acțiuni</th>
                                    </tr>
                                </thead>
                                {expenseMonths.map(({ key, label, list, totals }) => (
                                <tbody key={key} data-testid={`expense-month-${key}`}>
                                    <tr
                                        data-testid={`expense-month-toggle-${key}`}
                                        onClick={() => toggleExpenseMonth(key)}
                                        className="bg-zinc-100 border-y cursor-pointer hover:bg-zinc-200 transition-colors"
                                    >
                                        <td colSpan={7} className="px-4 py-2.5">
                                            <span className="flex items-center gap-2 font-semibold text-zinc-800">
                                                {(expandedMonths[key] || searchTerm || categoryFilter) ? <ChevronDown size={16} className="text-zinc-500" /> : <ChevronRight size={16} className="text-zinc-500" />}
                                                <Calendar size={15} className="text-zinc-500" />
                                                {label}
                                                <span className="text-xs font-normal text-zinc-500">({list.length} cheltuieli)</span>
                                            </span>
                                        </td>
                                    </tr>
                                    {(expandedMonths[key] || searchTerm || categoryFilter) && list.map((exp, idx) => (
                                        <tr 
                                            key={exp.id} 
                                            className={`border-b hover:bg-zinc-50 ${!exp.category ? 'bg-amber-50/50' : ''}`}
                                        >
                                            <td className="px-4 py-3 font-mono text-xs">{exp.nr_factura}</td>
                                            <td className="px-4 py-3">{exp.furnizor}</td>
                                            <td className="px-4 py-3 text-zinc-600">
                                                {exp.date ? new Date(exp.date).toLocaleDateString('ro-RO') : '-'}
                                            </td>
                                            <td className="px-4 py-3 text-right font-medium">
                                                {exp.suma?.toLocaleString('ro-RO')} RON
                                            </td>
                                            <td className="px-4 py-3 max-w-[200px] truncate">
                                                {exp.description || <span className="text-zinc-400 italic">-</span>}
                                            </td>
                                            <td className="px-4 py-3">
                                                {exp.category ? (
                                                    <span className="px-2 py-1 bg-violet-100 text-violet-700 rounded-full text-xs">
                                                        {exp.category}
                                                    </span>
                                                ) : (
                                                    <span className="text-amber-500 italic text-xs">Necategorizat</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-center">
                                                <div className="flex items-center justify-center gap-1">
                                                    <button
                                                        onClick={() => openEditDialog(exp)}
                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                        title="Editează"
                                                    >
                                                        <Edit3 size={16} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDelete(exp.id)}
                                                        className="p-1 text-red-600 hover:bg-red-50 rounded"
                                                        title="Șterge"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr data-testid={`expense-month-summary-${key}`} className="bg-violet-50/60 border-b-2 border-violet-200">
                                        <td colSpan={7} className="px-4 py-2.5">
                                            <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 text-xs">
                                                <span className="text-zinc-500 mr-auto">Total {label}</span>
                                                <span>Bază: <b data-testid={`expense-month-baza-${key}`} className="text-zinc-800">{fmtRon(totals.baza)}</b></span>
                                                <span>TVA: <b data-testid={`expense-month-tva-${key}`} className="text-zinc-800">{fmtRon(totals.tva)}</b></span>
                                                <span>Total General: <b data-testid={`expense-month-total-${key}`} className="text-violet-700 text-sm">{fmtRon(totals.total)}</b></span>
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                                ))}
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer with total */}
                {filteredExpenses.length > 0 && (
                    <div className="mt-4 bg-zinc-800 text-white rounded-xl p-4 flex justify-between items-center">
                        <span className="text-zinc-300">{filteredExpenses.length} cheltuieli afișate</span>
                        <span className="text-xl font-bold">{totalSum.toLocaleString('ro-RO')} RON</span>
                    </div>
                )}
            </div>

            {/* Edit Dialog */}
            <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Edit3 size={20} />
                            Editează Cheltuială
                        </DialogTitle>
                        <DialogDescription>
                            {editItem?.nr_factura} - {editItem?.furnizor}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        {/* Sume editabile - Baza, TVA, Total */}
                        <div className="bg-emerald-50 rounded-lg p-4 border border-emerald-200">
                            <p className="text-sm font-medium text-emerald-800 mb-3 flex items-center gap-2">
                                <DollarSign size={16} />
                                Valori Factură
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="text-xs text-emerald-600 font-medium uppercase">Bază</label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={editBaza}
                                        onChange={(e) => handleBazaChange(e.target.value)}
                                        className="mt-1 text-right font-mono"
                                        data-testid="edit-baza-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-emerald-600 font-medium uppercase">TVA</label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={editTva}
                                        onChange={(e) => handleTvaChange(e.target.value)}
                                        className="mt-1 text-right font-mono"
                                        data-testid="edit-tva-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs text-emerald-600 font-medium uppercase flex items-center gap-1">
                                        Total
                                        {autoCalculateTotal && (
                                            <span className="text-[10px] text-emerald-500 font-normal">(auto)</span>
                                        )}
                                    </label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        value={editTotal}
                                        onChange={(e) => handleTotalChange(e.target.value)}
                                        className={`mt-1 text-right font-mono font-bold ${
                                            autoCalculateTotal ? 'bg-emerald-100 border-emerald-300' : ''
                                        }`}
                                        data-testid="edit-total-input"
                                    />
                                </div>
                            </div>
                            {!autoCalculateTotal && (
                                <button
                                    onClick={() => {
                                        setAutoCalculateTotal(true);
                                        const baza = parseFloat(editBaza) || 0;
                                        const tva = parseFloat(editTva) || 0;
                                        setEditTotal((baza + tva).toFixed(2));
                                    }}
                                    className="text-xs text-emerald-600 hover:text-emerald-800 mt-2 underline"
                                >
                                    Reactivează calcul automat (Total = Bază + TVA)
                                </button>
                            )}
                        </div>
                        
                        {/* Info row */}
                        <div className="bg-zinc-50 rounded-lg p-3 text-sm">
                            <div className="flex justify-between">
                                <span className="text-zinc-500">Data facturii:</span>
                                <span>{editItem?.date ? new Date(editItem.date).toLocaleDateString('ro-RO') : '-'}</span>
                            </div>
                        </div>

                        <div>
                            <label className="text-sm font-medium">Descriere</label>
                            <Input
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                placeholder="Descrierea cheltuielii..."
                                className="mt-1"
                            />
                        </div>

                        <div>
                            <label className="text-sm font-medium">Categorie</label>
                            <div className="flex gap-2 mt-1">
                                <Input
                                    value={editCategory}
                                    onChange={(e) => setEditCategory(e.target.value)}
                                    placeholder="Ex: Transport, Utilități, Materiale..."
                                    className="flex-1"
                                    list="categories-list"
                                />
                                <datalist id="categories-list">
                                    {categories.map(cat => (
                                        <option key={cat} value={cat} />
                                    ))}
                                </datalist>
                            </div>
                            {categories.length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {categories.slice(0, 5).map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => setEditCategory(cat)}
                                            className={`px-2 py-1 text-xs rounded-full border ${
                                                editCategory === cat 
                                                    ? 'bg-violet-100 border-violet-300 text-violet-700' 
                                                    : 'bg-zinc-50 border-zinc-200 hover:bg-zinc-100'
                                            }`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="flex justify-end gap-2 pt-4">
                            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                                Anulează
                            </Button>
                            <Button onClick={handleSaveEdit} disabled={saving} className="bg-violet-600 hover:bg-violet-700">
                                {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Check size={14} className="mr-1" />}
                                Salvează
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            
            {/* Delete All Confirmation Dialog */}
            <Dialog open={showDeleteAllDialog} onOpenChange={setShowDeleteAllDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-red-600">
                            <AlertTriangle size={24} />
                            Ștergere Toate Cheltuielile
                        </DialogTitle>
                        <DialogDescription>
                            Ești sigur că vrei să ștergi <strong className="text-red-600">TOATE cele {expenses.length} cheltuieli</strong>?
                            <br />
                            <span className="text-red-500 font-medium">Această acțiune este ireversibilă!</span>
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <p className="text-sm text-red-700">
                                Pentru a confirma, scrie <strong className="font-mono bg-red-100 px-1 rounded">STERGE</strong> în câmpul de mai jos:
                            </p>
                        </div>
                        
                        <Input
                            placeholder="Scrie STERGE pentru a confirma"
                            value={deleteConfirmText}
                            onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                            className={`text-center font-mono text-lg ${
                                deleteConfirmText === 'STERGE' ? 'border-red-500 bg-red-50' : ''
                            }`}
                            data-testid="delete-confirm-input"
                        />
                        
                        <div className="flex justify-end gap-2 pt-2">
                            <Button 
                                variant="outline" 
                                onClick={() => {
                                    setShowDeleteAllDialog(false);
                                    setDeleteConfirmText('');
                                }}
                            >
                                Anulează
                            </Button>
                            <Button 
                                onClick={handleDeleteAll} 
                                disabled={deletingAll || deleteConfirmText !== 'STERGE'}
                                className="bg-red-600 hover:bg-red-700 text-white"
                                data-testid="confirm-delete-all-btn"
                            >
                                {deletingAll ? (
                                    <Loader2 className="animate-spin mr-1" size={14} />
                                ) : (
                                    <Trash2 size={14} className="mr-1" />
                                )}
                                Șterge Toate
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Cheltuieli;
